import React from 'react'
import { SideDrawer, SideDrawerForm } from '@sales-center/components'
import Typography from '@mui/material/Typography'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import Card from '@mui/material/Card'
import Switch from '@mui/material/Switch'
import FormControlLabel from '@mui/material/FormControlLabel'
import { useData } from '@user/components/Settings/Users/ReAssignRecords/hooks/useData'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import { BranchCard } from '@user/components/Settings/Users/ReAssignRecords/BranchCard'
import { useActions } from '@user/components/Settings/Users/ReAssignRecords/hooks/useActions'

interface Props {
  open: boolean
  onClose: (value: boolean) => void
  onReAssignSuccess: () => void
  userId: string
  isGuestReassignment?: boolean
  reassignLeads?: boolean
  reassignOpps?: boolean
}

const styles = {
  list: {
    listStyleType: 'disc',
    paddingX: '30px',
    paddingY: 0,
  },
  listItem: {
    display: 'list-item',
    p: 0,
    letterSpacing: '0.15px',
  },
}

export function ReAssignRecords(props: Props) {
  const {
    open,
    onClose,
    userId,
    onReAssignSuccess,
    isGuestReassignment,
    reassignOpps = true,
    reassignLeads = true,
  } = props

  const {
    status,
    groupedSalesTeams,
    usersByTeamId,
    fetchTeamsUsers,
    loadingTeamIds,
    showMore,
    allUnassigned,
    setAllUnassigned,
    formValid,
    setFormValid,
    onChangeShowMore,
  } = useData({
    userId,
    reassignOpps: Boolean(isGuestReassignment ? reassignOpps : true),
    reassignLeads: Boolean(isGuestReassignment ? reassignLeads : true),
  })

  const {
    onChangeSalesTeam,
    reAssignedTeams,
    onChangeOwner,
    onChangeAllUnassigned,
    handleClose,
    handleSubmit,
    reAssignStatus,
  } = useActions({
    groupedSalesTeams,
    allUnassigned,
    usersByTeamId,
    fetchTeamsUsers,
    setAllUnassigned,
    setFormValid,
    onClose,
    userId,
    onReAssignSuccess,
  })

  return (
    <SideDrawer open={open} onClose={handleClose} width={681}>
      <SideDrawerForm
        onSave={handleSubmit}
        onCancel={handleClose}
        secondaryBtnLabel="Cancel"
        padding="20px"
        primaryBtnLabel="Save"
        containerTop="0"
        disableSave={!formValid || reAssignStatus === 'pending'}
      >
        <Typography variant="h5" mb="12px" sx={{ fontSize: '22px' }}>
          Re-Assign Open Records
        </Typography>

        <Typography variant="subtitle3" pl="6px" pr="10px">
          {`You must re-assign all open leads and opportunities before
          ${isGuestReassignment ? "changing this user's role." : 'deactivating this user.'}`}
        </Typography>

        <List sx={styles.list}>
          <ListItem sx={styles.listItem}>
            {isGuestReassignment
              ? 'Closed records will stay assigned to this user.'
              : 'Closed records and activities will stay assigned to the user.'}
          </ListItem>
          <ListItem sx={styles.listItem}>
            Open activities will be re-assigned to the new owner of the record.
            {!showMore && (
              <span
                style={{ color: '#1565C0', cursor: 'pointer' }}
                onClick={onChangeShowMore}
              >
                ... more
              </span>
            )}
          </ListItem>
          {showMore && (
            <ListItem sx={styles.listItem}>
              Lead assignment rules targeted at this user will be disabled.
              <span
                style={{ color: '#1565C0', cursor: 'pointer' }}
                onClick={onChangeShowMore}
              >
                ... less
              </span>
            </ListItem>
          )}
        </List>

        {status === 'pending' ? (
          <Stack
            width="100%"
            justifyContent="center"
            alignItems="center"
            padding={3}
          >
            <CircularProgress />
          </Stack>
        ) : (
          <>
            <Card
              variant="outlined"
              sx={{
                paddingX: '4px',
                paddingY: '7px',
                mt: '10px',
                mb: '16px',
                backgroundColor: '#F9FAFB',
              }}
            >
              <FormControlLabel
                sx={{
                  mb: 0,
                  ml: 0,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '16px',
                  },
                }}
                labelPlacement="end"
                label="Update all New Owners below to be Unassigned"
                control={
                  <Switch
                    checked={allUnassigned}
                    onChange={onChangeAllUnassigned}
                  />
                }
              />
            </Card>

            <BranchCard
              groupedSalesTeams={groupedSalesTeams}
              reAssignedTeams={reAssignedTeams}
              usersByTeamId={usersByTeamId}
              loadingTeamIds={loadingTeamIds}
              onChangeSalesTeam={onChangeSalesTeam}
              onChangeOwner={onChangeOwner}
            />
          </>
        )}
      </SideDrawerForm>
    </SideDrawer>
  )
}
